// ── S4 — HYDRATE: Atlas's picks → the AtlasContext Forge receives ────────────────────────────────────
// docs/plans/atlas-forge-v3.md §3.4. PURE: no Angular, no RxJS, no I/O — so it is unit-testable against a
// cached grouped view with no running app, and portable if a server-side caller ever appears.
//
// Atlas emits `{ id, why }[]` and NOTHING ELSE. Everything factual — token, type, scope, description, the
// owning entity — is looked up HERE, out of the same grouped view Atlas selected from. That is what makes
// the seam safe: Atlas cannot hallucinate a token, a type, a scope or an owner, because it never states
// one. It can only name an id, and an id that is not in the view is an ERROR, not a bad step.
//
// The only free text in the entire seam is `why`.

import {
  GroupedView, GroupedEntity, GroupedField, GroupedVariant, EntityHolder, StandaloneItem,
} from './atlas-grouped-view.util';
import { AtlasContext, AtlasContextEntry, AtlasContextQueryField, AtlasContextReference } from './forge.types';

/** What Atlas returns: a list of picked ids, each with one sentence. See v3 §3.2 for what `why` must say. */
export interface AtlasSelection {
  picks: { id: string; why: string }[];
}

export interface HydrateResult {
  context: AtlasContext;
  /**
   * Unknown ids and any other refusal. Returned, never thrown and never silently dropped: the caller
   * decides whether to re-prompt Atlas or surface it. Same rule as the grouped view's `stats` — a pick
   * that vanishes without trace is indistinguishable from a pick that was never made.
   */
  errors: string[];
  /**
   * Things withheld for a DATA reason rather than a mistake — currently reference columns whose target
   * entity this page does not import. Not errors (nobody did anything wrong) but not silent either: the
   * same discipline as the grouped view's `droppedUnimported`. "No such column here" must be
   * distinguishable from "the hydrator lost it".
   */
  notes: string[];
}

// ── the index: every place an id can live in a grouped view ──────────────────────────────────────────
// Miss one of these five and a legitimate pick silently becomes "unknown id".
export type Found =
  | { kind: 'field'; variant: GroupedVariant; field: GroupedField; root: GroupedEntity }
  | { kind: 'holder'; holder: EntityHolder; root: GroupedEntity }
  | { kind: 'entity'; entity: GroupedEntity }
  | { kind: 'standaloneVar'; variant: GroupedVariant }
  | { kind: 'item'; item: StandaloneItem; group: string };

export function indexView(view: GroupedView): Map<string, Found> {
  const ix = new Map<string, Found>();
  const put = (id: string | undefined, f: Found) => { if (id && !ix.has(id)) ix.set(id, f); };

  for (const root of view.entities || []) {
    put(root.id, { kind: 'entity', entity: root });
    for (const h of root.heldBy || []) put(h.id, { kind: 'holder', holder: h, root });
    const walk = (f: GroupedField) => {
      for (const v of f.variants || []) put(v.id, { kind: 'field', variant: v, field: f, root });
      for (const sub of f.fields || []) walk(sub);   // nesting lives in the field, not the entity
    };
    for (const f of root.fields || []) walk(f);
  }
  for (const v of [...(view.localVars || []), ...(view.globalVars || [])]) {
    put(v.id, { kind: 'standaloneVar', variant: v });
  }
  for (const g of view.standalone || []) {
    for (const it of g.items || []) put(it.id, { kind: 'item', item: it, group: g.group });
  }
  return ix;
}

// ── synthetic ids ────────────────────────────────────────────────────────────────────────────────────
// MUST NOT collide with a minted map id. Reconcile mints `$<PAGECODE><n>` where PAGECODE is bijective
// base-26 UPPERCASE (A, B, … Z, AA, AB…), plus `$<n>` for globals. So `$E1` and `$Q1` ARE REAL IDS — "E" is
// the 5th page code and is already in use in the benchmark sample; "Q" is the 17th. An underscore cannot
// appear in a minted id, so it is the safe separator. (This replaced an earlier `$E1`/`$Q1` convention that
// would have had resolveTokens rewrite the wrong variable.)
// RETIRED (P5, 2026-07-29) — column ids are no longer minted here. They are stamped on each entity's own
// TypeData (`atlasColumnId` = `$Q<e>_<n>`, P1/P2) and composed into a path at grouped-view build time
// (`GroupedField.columnId` = `$Q1_7.$Q2_7`), so `collectColumns` READS one instead of making one up.
//
// Why it had to go: this counter restarted at 1 on every payload, so the same column had a different id
// each time — uncacheable, unbenchmarkable, and impossible for Atlas to reference. And because minting
// and emitting happened in the SAME walk, "which columns exist" was the same event as "which columns are
// sent", which is how one render produced 149 columns against 1 variable.
//
// Kept commented rather than deleted: it is the shape any future per-payload id would take, and the
// reason it must not come back is only legible next to it.
//   const QUERY_COLUMN_ID = (n: number) => `$Q_${n}`;
/**
 * Fallback handle for an entity root with no minted id. RENAMED from `$E_<n>` to `$ESYN_<n>`
 * (2026-07-29) — `$E_<n>` is now a REAL, PERSISTED entity handle (`Entity.atlasEntityId`, backfilled
 * onto 220 docs across 21 clients), and this counter restarts at 1 on every payload, so it would have
 * minted `$E_1` for one entity while a different entity genuinely IS `$E_1`. Two entities, one id, same
 * `entities[]` array.
 *
 * Latent while nothing reads `atlasEntityId`; live the moment P5 wires it in. Same failure as the
 * `$Q1`/`$E1`-vs-page-code collision this contract already fixed once, which is why the harness now
 * asserts on the shape rather than trusting the prefix.
 *
 * EXPECTED TO GO AWAY. It exists because 6 of 52 entity roots had no map id; after the P2 backfill every
 * entity carries an `atlasEntityId`, so P5 should be able to turn this into an assertion that it never
 * fires. See docs/plans/atlas-entity-column-ids.md.
 */
const SYNTHETIC_ENTITY_ID = (n: number) => `$ESYN_${n}`;
// A `$P_n` prefix for NAVIGATE targets was tried and REVERTED 2026-07-28. A page is already a minted,
// pinned handle (`$20 → budjetpage`), so re-labelling it would have been the pipeline's only re-mint — and
// the cost showed up immediately: check-hydrate keys its results by the id that was PICKED, so an entry
// coming back under a different id broke the correspondence outright. Pages need no special casing; the
// entry already reads `$20 (NAVIGATE) — budjetpage`, and `scope` is what marks it as a page.

/**
 * The only types you can put in a WHERE or an ORDER BY. Deliberately an ALLOW-list, not a deny-list of
 * containers: a container type added later would silently become a bogus column under a deny-list.
 */
const SCALAR_TYPES = new Set(['String', 'TimeStamp', 'Number', 'Boolean', 'GEOMETRY']);

/**
 * Collect an entity's filterable/sortable columns — leaf-first, dotted for nested ones (`add_condition`
 * stores its column as `data.path = field.split('.')`, so "metaFormRef.pageRef.accessToken" is legal).
 *
 * SCALARS ONLY (2026-07-28). A container — an entity list, an AOB reference, an AAOB field — is not a
 * column: `WHERE transaction _eq x` is meaningless when `transaction` is a list, and an AAOB is filtered
 * through its own mechanism (`aaobField` + `key`), not as a plain column. But a container IS the ROUTE to
 * columns, so we never emit it and always descend through it.
 *
 * This also removes a real ambiguity rather than papering over it: taking the type from `variants[0]` was
 * wrong on 29 of 1,397 fields (2.1%) and arbitrarily so, since it depended on variant order —
 * `Budget.transaction` is REFERENCE_ENTITY_VARIABLE_AOB via one holder and ENTITY_LIST_VARIABLE via
 * another. Every one of those disagreements is a container, so excluding containers resolves all of them.
 */
// `refIdOf` maps a referenced entity id -> its MINTED handle, registering it in entities[] as a side
// effect. Both halves matter: `refEntity` MUST live in the same id space as `of` / `holdsEntity`, or the
// model cannot see that "$Q_1 wants an ootbLeads" and "$2 holds $AC242" are the same entity — and a
// two-hop join through `entities[].token` is not a join it should have to make.
/**
 * PLATFORM COLUMNS A FORGE RULE DEPENDS ON — sent whenever their entity is queried, picked or not (owner, 2026-10-05).
 * Forge's rule 12 keeps a list of the people in a workspace to those whose systemRemovedAt is empty; the picker has no
 * reason to choose that column for "load the people", and an absent column is a filter Forge cannot write. Narrowing
 * stays strict for everything else (see the note on collectColumns). Top-level fields only.
 */
const ALWAYS_SENT_COLUMNS: Readonly<Record<string, readonly string[]>> = { systemOotbUser: ['systemRemovedAt'] };

function collectColumns(
  entity: GroupedEntity,
  refIdOf: (refEntityId: string) => string | undefined,
  note: (msg: string) => void,
  // ── ONLY ATLAS'S PICKED COLUMNS ARE SENT (2026-08-05 → reverted 08-09 → RE-APPLIED 08-09) ────────────
  // docs/plans/atlas-picks-columns.md. Strict: no fallback, no "send everything when nothing was picked".
  //
  // THE ROUND TRIP IS THE ARGUMENT, so it is recorded rather than summarised. Narrowing was reverted the
  // same day it was measured, because a column Atlas did not pick is simply ABSENT, and Forge asked for an
  // absent column only ~70% of the time — silently dropping the filter or sort the other 30% (V22,
  // controlled A/B, 16 runs). A dropped WHERE returns every row; a dropped ORDER BY returns them
  // unordered. Neither errors. At that reliability the narrowing was not affordable.
  //
  // WHAT CHANGED IS THE ASK, not the narrowing. The plan prompt had made producing the job and refusing a
  // footnote — 42 lines pushing to produce against 15 about anything missing, the CONTEXT_REQUEST section
  // 84% of the way in, and rule 2 offering "add a WARNING line" as a cheaper way to comply with both. Once
  // refusal became half the stated job and the warning escape was closed, the same A/B went to 16 of 16
  // asked, 0 dropped. The recovery is now reliable enough to build on.
  //
  // AND THE FALLBACK STAYS GONE. It only ever fired on a ZERO-column pick, so it guarded the case where
  // Atlas engaged least and left a PARTIAL pick — the likelier one — fully exposed. A guard that covers
  // the rare failure and not the common one is false confidence, not safety.
  //
  // Accuracy was never the reason either way: across fourteen runs, at 49 columns and at 1, Forge chose
  // V08's dotted reference column correctly every time. This buys tokens, and it is affordable now.
  pickedColumns?: Set<string>,
): AtlasContextQueryField[] {
  const out: AtlasContextQueryField[] = [];
  const keep = (id: string) => !!pickedColumns?.has(id);
  const always = new Set(ALWAYS_SENT_COLUMNS[entity.token || ''] || []);
  const walk = (f: GroupedField, path: string[]) => {
    const here = [...path, f.field];
    // THE PATH THROUGH A REFERENCE IS THE HASURA RELATIONSHIP NAME (2026-08-05).
    //
    // `here` addresses THIS node — for a reference that is the raw FK column, which is right, because the
    // compare-by-id leaf filters the FK itself (`{ executiveId: { _eq } }`). But anything BELOW a reference
    // is reached through the relationship, `<referencedEntityId>_<fkColumn>` — the same name the SELECT
    // uses (crud.service joinKey). Composing children from the raw name produced
    // `executiveId.territoryId` where the block needs `salesExecutive_MTA5Mj_executiveId.territoryId`;
    // Hasura then rejects the `where` ("field not found in type uuid_comparison_exp") and THE QUERY
    // SILENTLY RETURNS UNFILTERED ROWS. Measured before this fix: 1,175 of 1,778 leaf columns across the
    // 17 corpus maps — 66% — resolved to the unusable spelling. See condition-targets.ts, which has always
    // spelled it this way for the human picker, and docs/plans/reference-drill-column-path.md.
    //
    // NOT applied to AOB hops: both walks use the raw name there, and they agree.
    const childPath = f.refEntity ? [...path, `${f.refEntity}_${f.field}`] : here;
    const variants = f.variants || [];
    // THE ID IS READ, NOT MINTED (P5, 2026-07-29). `columnId` is composed at grouped-view build time
    // from handles stamped on each entity's own TypeData — permanent, project-stable, and the same
    // string on every payload. It replaced `QUERY_COLUMN_ID(++col)`, which re-minted per payload and so
    // could never be cached, benchmarked, or referenced by Atlas.
    //
    // NO id means the column is deliberately unofferable, and both cases are already decided:
    //   · an AAOB subtree — stamped for identity, never offered (aaobField + key is its mechanism)
    //   · an unstamped link in the chain — an entity not re-saved since P1, or a client the P2 backfill
    //     did not cover (prod MjU0OD). Composition is all-or-nothing, so a half-path never appears.
    // Skipping is therefore correct in both, and needs no special case here.
    if (!f.columnId) { for (const sub of f.fields || []) walk(sub, childPath); return; }
    if (variants.length && variants.every((v) => SCALAR_TYPES.has(v.type))) {
      if (keep(f.columnId) || (path.length === 0 && always.has(f.field))) out.push({ id: f.columnId, field: here.join('.'), type: variants[0].type, description: f.description || '' });
    } else if (!variants.length && f.columnType && SCALAR_TYPES.has(f.columnType)) {
      // FILTERABLE BUT NOT READABLE (2026-08-05). A column placed from the SKELETON rather than from a
      // matched token — a scalar inside an AOB that hangs off a reference, e.g.
      // `ootbLeads.metaFormRef.questions.key`. Nothing mints a Blockly variable for it, so it has no
      // variant to take a type from, but it is stamped and legal to filter on, and the human picker offers
      // it. Without this branch the node reaches the tree and is then dropped here for having no variants —
      // which is the bug this closes, not a guard.
      if (keep(f.columnId) || (path.length === 0 && always.has(f.field))) out.push({ id: f.columnId, field: here.join('.'), type: f.columnType, description: f.description || '' });
    } else if (f.aaob) {
      // AN AAOB IS A FILTERABLE COLUMN (2026-08-04, G2). `add_condition` / `add_order` name it in the SAME
      // `field` slot as any other column, then add `aaobKey` (which runtime label) and `aaobType` (which of
      // the four value columns). Withholding it left those two blocks — the only consumers of this lane —
      // with no id to name, so an AAOB filter or sort had NO valid spelling: not an id, not a `$Q` (none
      // was offered), and a typed name is the `leadRefference` guess.
      //
      // Its variants are AAOB_FIELD / list types, never scalars, so the scalar branch above cannot reach
      // it — the same reason a reference needs its own branch. Type is 'AAOB' rather than a value type
      // because the value type is per-condition (`aaobType`), not a property of the column.
      if (keep(f.columnId)) out.push({ id: f.columnId, field: here.join('.'), type: 'AAOB', description: f.description || '', aaob: true });
    } else if (f.refEntity) {
      // A REFERENCE is filterable in its own right, and it is how you fetch the children of a parent:
      //     {"field":"leadRefference","op":"_eq","value":{"var":"globalVariable$editOotbLeadsEntity"}}
      // It has NO variants — nothing addresses the link itself — so the scalar test above can never reach
      // it, which is why it needs its own branch. 5 of the 33 query-field sites in the corpus are this.
      // `refEntity` says which entity the compared variable must hold; without it the model would know a
      // reference is filterable but not what to compare it against.
      const target = refIdOf(f.refEntity);
      // No handle means the referenced entity is not imported on this page, so the grouped view has no
      // data for it and no variable here could ever hold one. The column would name something the model
      // cannot compare against, so it is withheld — but COUNTED, never dropped in silence.
      if (target && keep(f.columnId)) out.push({ id: f.columnId, field: here.join('.'), type: 'ENTITY_REFERENCE', refEntity: target, description: f.description || '', ...(f.linkId ? { linkId: f.linkId, linkToken: f.linkToken } : {}) });
      else note(`${entity.name}.${here.join('.')} → ${f.refEntity}: reference column withheld, that entity is not imported on this page`);
    }
    for (const sub of f.fields || []) walk(sub, childPath);   // descend regardless — scalars live inside refs
  };
  for (const f of entity.fields || []) walk(f, []);
  return out;
}

/**
 * Hydrate Atlas's picks into the payload Forge receives.
 *
 * `entities[]` is CLOSED over the variables: it holds every entity Atlas picked directly PLUS every entity
 * referenced by an emitted `of` / `holdsEntity`. That makes the invariant "every `of` appears in
 * entities[]" true by construction — which is what allowed `ofName` to be dropped from each entry.
 *
 * Query columns are attached to those entities and NO OTHERS (decided 2026-07-28). Forge chooses the
 * filter/sort column when it composes the query — that is step-writing, not selection, and a column is not
 * a variable so Atlas has no handle for one. Every column carries an id, so a hallucinated column name is
 * structurally impossible rather than caught after the fact. Cost is ~40 short entries for the widest
 * entity in the corpus (ootbLeads), and only for entities already in the payload.
 */
export function hydrateAtlasContext(view: GroupedView, selection: AtlasSelection): HydrateResult {
  const ix = indexView(view);
  const viewEntityByToken = new Map<string, GroupedEntity>();
  for (const e of view.entities || []) if (e.token) viewEntityByToken.set(e.token, e);
  const errors: string[] = [];
  const notes: string[] = [];
  const variables: AtlasContextEntry[] = [];

  // QUERY TARGETS — entities that earn `queryColumns`. Only an `entity` PICK lands here.
  const neededEntities = new Map<string, GroupedEntity>();
  const needEntity = (root: GroupedEntity) => { if (root.token) neededEntities.set(root.token, root); };
  /**
   * IDENTITY-ONLY entities — named and joinable, but with NO columns (P5 change 2, 2026-07-29).
   *
   * A `field` or `holder` pick used to call `needEntity`, so picking a variable that merely HOLDS a lead
   * bought ootbLeads' 69 columns. Holding is not querying: `$2` is the VALUE in
   * `WHERE leadRefference _eq $2`, and filling that slot needs no knowledge of what columns a lead has.
   * Measured on a real payload: 143 columns, of which 69 were bought this way.
   *
   * The rule was already written four lines below where it was being broken — "An entity earns
   * queryColumns by being queried, not by being pointed at". The `entity` pick kind exists precisely to
   * say "this function operates on this entity", and that is now the only thing that grants columns.
   *
   * These still MUST appear in `entities[]`: `of` / `holdsEntity` / `refEntity` all join into it, and the
   * invariant "every `of` appears in entities[]" is what let `ofName` be dropped from every entry.
   */
  const identityEntities = new Map<string, GroupedEntity>();
  const needIdentity = (root: GroupedEntity) => { if (root.token) identityEntities.set(root.token, root); };

  // `root.id` is now the entity's PERSISTED `$E_<n>` (P5) — the grouped view prefers `atlasEntityId` and
  // only falls back to the page-minted `entityName` id for an entity the P2 backfill did not reach.
  //
  // The synthetic branch below should therefore be DEAD. It was measured at 6 of 52 roots before the
  // backfill; every entity in a stamped client now carries a handle, so a `$ESYN_` id appearing in a
  // payload means an unstamped entity slipped through — reported as a NOTE rather than passing silently,
  // because a synthetic handle resolves to nothing downstream (`resolveTokens` has no entry for it) and
  // would leave the model naming an entity that does not exist.
  let synthEntity = 0;
  const entityIdOf = new Map<string, string>();
  const idForEntity = (root: GroupedEntity): string => {
    const key = root.token || root.name;
    let id = entityIdOf.get(key);
    if (!id) {
      id = root.id;
      if (!id) {
        id = SYNTHETIC_ENTITY_ID(++synthEntity);
        notes.push(`${root.name}: no persisted $E_n — synthetic ${id}. Entity is unstamped (client not backfilled?); it will not resolve.`);
      }
      entityIdOf.set(key, id);
    }
    return id;
  };

  // ── COLUMN AND LINK PICKS (2026-08-08) ───────────────────────────────────────────────────────────────
  // `$Q…` and a reference `linkId` are pickable now that the pick list renders them, and they resolve
  // DIFFERENTLY from every other pick: they are not variables and they do not belong in `variables[]`.
  // A column is a property of the entity that owns it, applied when its `queryColumns` are collected.
  //
  // The index above cannot serve — it is keyed by variant/holder/entity id, and a column id is a fourth
  // space. So they are separated here BEFORE the switch, or every one of them lands in the `unknown id`
  // branch and the run reports Atlas hallucinating handles it was explicitly shown.
  const columnPicks = new Map<string, Set<string>>();   // entity key → picked column ids
  /**
   * entity key → the reference links picked on it.
   *
   * THIS USED TO BE A `Set<string>` THAT NOTHING READ. It was filled here and its only other appearance
   * was the `continue` twelve lines below, which skipped link picks so they would not be reported as
   * unknown ids — so picking a link produced exactly the same payload as not picking it, with no entry,
   * no error and no note. Benchmark 0.1 V05 created an orphaned record in 5 of 5 runs; Atlas had picked
   * the link in an earlier round and it went nowhere.
   *
   * Links now land in their own payload lane (`entities[].references`) rather than riding on the
   * reference COLUMN — see the note on `AtlasContextReference`. Keyed by entity because that is how the
   * payload is shaped, and resolved AFTER the walk so a link and a column on the same field cannot race.
   */
  const linkPicks = new Map<string, Set<string>>();
  {
    const colOwner = new Map<string, string>();         // column id → owning entity key
    const linkOwner = new Map<string, string>();        // link id  → owning entity key
    for (const root of view.entities || []) {
      const key = root.token || root.name;
      const walk = (f: GroupedField) => {
        if (f.columnId) colOwner.set(f.columnId, key);
        if (f.linkId) linkOwner.set(f.linkId, key);
        for (const sub of f.fields || []) walk(sub);
      };
      for (const f of root.fields || []) walk(f);
    }
    for (const pick of selection.picks || []) {
      const owner = colOwner.get(pick.id);
      if (owner) {
        // Picking a column IS asking to query that entity — you cannot filter what you are not reading.
        // Registering it here is what makes "pick $Q28_7" sufficient without also picking the entity.
        const ent = (view.entities || []).find((e) => (e.token || e.name) === owner);
        if (ent) needEntity(ent);
        (columnPicks.get(owner) ?? columnPicks.set(owner, new Set()).get(owner)!).add(pick.id);
        continue;
      }
      const linkHome = linkOwner.get(pick.id);
      if (linkHome) {
        // A link is a WRITE handle, so picking one says this entity is written — not queried. That earns
        // it a place in `entities[]` (CREATE_ENTITY needs the id) but NOT a column list: `needIdentity`,
        // not `needEntity`. Handing a write-only function forty filter columns is the bloat the
        // narrowing work removed.
        const ent = (view.entities || []).find((e) => (e.token || e.name) === linkHome);
        if (ent) needIdentity(ent);
        (linkPicks.get(linkHome) ?? linkPicks.set(linkHome, new Set()).get(linkHome)!).add(pick.id);
      }
    }
  }

  for (const pick of selection.picks || []) {
    // Already consumed above — a column/link pick is not a variable and must not fall through to the
    // switch, which would file it as unknown.
    if ([...columnPicks.values()].some((s) => s.has(pick.id))
        || [...linkPicks.values()].some((s) => s.has(pick.id))) continue;
    const found = ix.get(pick.id);
    if (!found) { errors.push(`unknown id ${pick.id} — not present in this page's grouped view`); continue; }

    switch (found.kind) {
      case 'field': {
        needIdentity(found.root);   // reading a field is not querying the entity — identity only
        variables.push({
          id: found.variant.id,
          token: found.variant.token || '',
          type: found.variant.type,
          scope: found.variant.scope,
          // The MEANING lives on the field and is shared by every variant of it — the variant's own
          // `description` is a usage summary, which is a selection signal and stays upstream.
          description: found.field.description || '',
          of: idForEntity(found.root),
          why: pick.why,
        });
        break;
      }
      case 'holder': {
        needIdentity(found.root);   // HOLDING an entity is not querying it — identity only
        variables.push({
          id: found.holder.id,
          token: found.holder.token || '',
          type: found.holder.type,
          scope: found.holder.scope,
          description: found.holder.meaning || '',
          holdsEntity: idForEntity(found.root),
          why: pick.why,
        });
        break;
      }
      case 'standaloneVar': {
        variables.push({
          id: found.variant.id,
          token: found.variant.token || '',
          type: found.variant.type,
          scope: found.variant.scope,
          description: found.variant.meaning || '',
          why: pick.why,
        });
        break;
      }
      case 'item': {
        // entityName / AAOB / NAVIGATE. No description exists for these — and for a NAVIGATE target the
        // token IS the page name, which carries the meaning, so falling back to it is not a placeholder.
        variables.push({
          id: found.item.id,
          token: found.item.token || '',
          type: found.item.type,
          scope: found.item.scope,
          description: found.item.token || found.group,
          why: pick.why,
        });
        break;
      }
      case 'entity': {
        // An entity is not a variable — it belongs in entities[], not variables[]. Picking one is how Atlas
        // says "this function operates on this entity" (CREATE_ENTITY / QUERY name it).
        needEntity(found.entity);
        idForEntity(found.entity);
        break;
      }
    }
  }

  // Referenced entities are pulled in so a reference column has something to point AT — but WITHOUT
  // columns of their own. An entity earns queryColumns by being queried, not by being pointed at; giving
  // ootbLeads its 79 columns just because a task links to it would be exactly the pick-list this design
  // rejects. `refTargets` holds those identity-only entries.
  const refTargets = new Map<string, GroupedEntity>();
  const refIdOf = (refEntityId: string): string | undefined => {
    if (!refEntityId) return undefined;
    for (const e of neededEntities.values()) if ((e.token || e.name) === refEntityId) return idForEntity(e);
    const known = viewEntityByToken.get(refEntityId);
    if (!known) return undefined;                       // not imported on this page — see collectColumns
    refTargets.set(refEntityId, known);
    return idForEntity(known);
  };

  /**
   * The picked reference links on one entity, as its own payload lane.
   *
   * Walks the same tree `collectColumns` does but keys on `linkId`, and — unlike the column walk — is NOT
   * gated by anything else being picked. That independence is the whole change: attaching a relationship
   * and filtering a table are different actions, so needing one must never require selecting the other.
   *
   * `refEntity` resolves through `refIdOf`, which registers the target in `refTargets` as a side effect —
   * so the entity the paired `entityVar` must hold is guaranteed to appear in `entities[]`. Without that
   * the model would be handed a link pointing at a name it cannot see. A link whose target is not on this
   * page cannot be honoured at all and is reported rather than emitted half-formed.
   */
  const collectReferences = (entity: GroupedEntity, picked?: Set<string>): AtlasContextReference[] => {
    const out: AtlasContextReference[] = [];
    if (!picked?.size) return out;
    const walk = (f: GroupedField, path: string[]): void => {
      const here = [...path, f.field];
      if (f.linkId && picked.has(f.linkId)) {
        const target = f.refEntity ? refIdOf(f.refEntity) : undefined;
        if (target) {
          out.push({
            id: f.linkId, token: f.linkToken || '', field: here.join('.'),
            refEntity: target, description: f.description || '',
          });
        } else {
          notes.push(`${entity.name}.${here.join('.')} → ${f.refEntity}: reference link picked, but that entity is not on this page, so nothing here could hold one — link withheld`);
        }
      }
      for (const sub of f.fields || []) walk(sub, f.refEntity ? [...path, `${f.refEntity}_${f.field}`] : here);
    };
    for (const f of entity.fields || []) walk(f, []);
    return out;
  };

  // `let col = 0` retired with QUERY_COLUMN_ID — nothing counts columns per payload any more.
  const queried = [...neededEntities.values()].map((e) => ({
    id: idForEntity(e),
    token: e.token || e.name,
    name: e.name,
    description: e.description,
    status: e.status,
    queryColumns: collectColumns(e, refIdOf, (m: string) => notes.push(m), columnPicks.get(e.token || e.name)),
    references: collectReferences(e, linkPicks.get(e.token || e.name)),
    // Says WHICH kind of empty this is. An entity with no columns renders as a bare line in the Forge
    // payload, and silence there meant both "this entity has no filterable columns" and "columns exist,
    // none were selected" — two states, one rendering, so the model guessed. Measured: 3 of 5 runs asked
    // for the missing sort column, 2 silently dropped the ORDER BY. Only the hydrator knows which case it
    // is, because only the hydrator decides whether to narrow.
    // ALWAYS true: narrowing is unconditional, so an absent column is ALWAYS a selection gap and never
    // proof the entity has none. The payload has to SAY that — silence would read as "no such columns".
    columnsNarrowed: true,
  }));
  // IDENTITY-ONLY: entities named by a reference column (`refTargets`) OR by a field/holder pick's
  // `of` / `holdsEntity` (`identityEntities`). Both must appear in `entities[]` — the invariant "every
  // `of` appears in entities[]" is what let `ofName` be dropped from every entry — and NEITHER earns
  // columns. Deduped against the query targets so a picked entity is never listed twice.
  const identityOnly = new Map<string, GroupedEntity>();
  for (const [k, e] of refTargets) if (!neededEntities.has(k)) identityOnly.set(k, e);
  for (const [k, e] of identityEntities) if (!neededEntities.has(k)) identityOnly.set(k, e);
  // An identity-only entity still carries its picked LINKS. Picking a link means this entity gets
  // WRITTEN, which is identity-only by design (a write needs no filter columns) — so if the lane were
  // attached to `queried` alone, the commonest case for a link, a plain create, would drop it again.
  const pointedAt = [...identityOnly.values()]
    .map((e) => ({
      id: idForEntity(e), token: e.token || e.name, name: e.name, description: e.description, status: e.status,
      references: collectReferences(e, linkPicks.get(e.token || e.name)),
    }));

  return { context: { entities: [...queried, ...pointedAt], variables }, errors, notes };
}
